import {
  DiscountPackage,
  SymfonyCheckPromoCodeClient,
  POSDiscountPersistenceStrategy,
  POSExternalVoucherProvider,
  VoucherPersistenceStrategy,
} from "@smeetz/discount-common";
import { initGroupOffersClient } from "@smeetz/offers-common";
import { useGroupOffersStore } from "@/stores/group-offers.store";
import { LogLevel, LogService } from "@/services/logging";

// No-op strategy — POS has no backend DB. Bucket-split still works because
// VoucherFlow mutates session.vouchers[] regardless of whether persistence
// fires.
const noopVoucherStrategy: VoucherPersistenceStrategy = {
  async upsertVoucher() {
    /* in-memory only */
  },
  async deleteVoucher() {
    /* in-memory only */
  },
};

/**
 * Initializes the discount package's clients and persistence strategies.
 * Never throws — it runs synchronously inside appSetup, and a throw would
 * abort the whole bootstrap. On failure the root cause is journaled here and
 * later discount operations fail with the package's own errors.
 */
export function initializeDiscountService(): void {
  try {
    const missingEnv = Object.entries({
      VITE_B2B_API: import.meta.env.VITE_B2B_API,
      VITE_SERVICES_API: import.meta.env.VITE_SERVICES_API,
      VITE_X_API_KEY: import.meta.env.VITE_X_API_KEY,
    })
      .filter(([, value]) => !value)
      .map(([name]) => name);
    if (missingEnv.length) {
      throw new Error(
        `Missing environment variables: ${missingEnv.join(", ")}`
      );
    }

    DiscountPackage.initCheckPromoCodeClient(
      new SymfonyCheckPromoCodeClient({
        baseUrl: import.meta.env.VITE_B2B_API as string,
        apiKey: import.meta.env.VITE_X_API_KEY as string,
      })
    );

    DiscountPackage.initExternalVoucherProvider(
      new POSExternalVoucherProvider({
        baseUrl: import.meta.env.VITE_SERVICES_API as string,
        apiKey: import.meta.env.VITE_X_API_KEY as string,
      })
    );

    DiscountPackage.initDiscountPersistenceStrategy(
      new POSDiscountPersistenceStrategy({
        baseUrl: import.meta.env.VITE_SERVICES_API as string,
        apiKey: import.meta.env.VITE_X_API_KEY as string,
      })
    );

    DiscountPackage.initVoucherPersistenceStrategy(noopVoucherStrategy);

    initGroupOffersClient({
      /**
       * Delegates to the group-offers store, which returns cached offers when
       * available and only hits the API on a cold cache.
       */
      getGroupOffers: (groupId: number) => {
        const store = useGroupOffersStore();
        return store.getOffers(groupId);
      },
    });
  } catch (error) {
    // Swallowed on purpose (bootstrap must survive); console.error keeps dev
    // visibility while LogService ships it.
    console.error("DiscountService initialization failed:", error);
    LogService.info(
      "DiscountService initialization failed",
      { error },
      LogLevel.Error
    );
  }
}
